import { useEffect, useRef, useState } from 'react';
import stringUtils from '../../utils/stringUtils';
import { Box, Icon, Image } from '@zillow/constellation-10';
import { IconHomeTypeFilled } from '@zillow/constellation-icons';
import { AvatarGroup } from '@zg-rentals/satellite-components';
import { thumbnailBackgroundStyles, thumbnailBorderRadius, thumbnailSizeStyles } from './styles';

type Props = {
  avatarName?: string | null;
  photoUrl?: string | null;
};

export default function PropertyThumbnailWithAvatar({ avatarName = '', photoUrl }: Props) {
  const capitalizedAvatarName = avatarName ? stringUtils.capitalize(avatarName) : '';
  const imageRef = useRef<HTMLImageElement | null>(null);
  // Load results are keyed by URL so a photoUrl change hides the stale image in the same render
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  // Rescue images that completed before React attached onLoad (e.g. cached or SSR-rendered)
  useEffect(() => {
    const image = imageRef.current;
    if (photoUrl && image?.complete && image.naturalWidth > 0) {
      setLoadedUrl(photoUrl);
    }
  }, [photoUrl]);

  const isImageVisible = Boolean(photoUrl) && loadedUrl === photoUrl;

  return (
    <Box css={{ position: 'relative', alignSelf: 'flex-start', display: 'flex' }}>
      {photoUrl && failedUrl !== photoUrl ? (
        <Image
          ref={imageRef}
          src={photoUrl}
          alt="Property thumbnail"
          width={56}
          height={56}
          onLoad={() => setLoadedUrl(photoUrl)}
          onError={() => setFailedUrl(photoUrl)}
          css={{
            overflow: 'hidden',
            flexShrink: 0,
            ...thumbnailBorderRadius,
            ...thumbnailSizeStyles,
            // Unpainted until this URL loads so the browser's broken-image glyph never flashes
            ...(isImageVisible ? {} : { display: 'none' }),
          }}
        />
      ) : null}
      {!isImageVisible && (
        <Box
          data-testid="property-thumbnail-placeholder"
          css={{
            ...thumbnailBorderRadius,
            ...thumbnailBackgroundStyles,
            ...thumbnailSizeStyles,
          }}
        >
          <Icon css={{ color: 'icon.subtle' }} render={<IconHomeTypeFilled />} size="lg" />
        </Box>
      )}

      <Box css={{ position: 'absolute', right: '-200', bottom: '-200' }}>
        <AvatarGroup participants={[capitalizedAvatarName]} variant="compact" />
      </Box>
    </Box>
  );
}
